import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useStaticQuery, graphql } from 'gatsby';
import ScrollMagic from 'scrollmagic';
import {
  Button,
  Close,
  List,
  ListItem,
  Modal,
  ModalBody,
} from 'chaoskit/src/components';

import { Data } from '../../components';
import { config } from '../../helpers/config';

import overview from '../../assets/media/product/scenarios/overview.svg';

import blackhole from '../../assets/media/product/scenarios/icon-blackhole.svg';
import cpu from '../../assets/media/product/scenarios/icon-cpu.svg';
import dns from '../../assets/media/product/scenarios/icon-dns.svg';
import latency from '../../assets/media/product/scenarios/icon-latency.svg';
import packetLoss from '../../assets/media/product/scenarios/icon-packet-loss.svg';
import shutdown from '../../assets/media/product/scenarios/icon-shutdown.svg';

import buildShare from '../../assets/media/product/scenarios/build-share.svg';

import ChooseHosts from './ChooseHosts';
import dialUpBlob from '../../assets/media/product/scenarios/dial-up-background.svg';
import AttackConfig from './AttackConfig';
import magnitudeBlob from '../../assets/media/product/scenarios/magnitude-background.svg';

import hypothesizeObserve from '../../assets/media/product/scenarios/hypothesize-observe.svg';
import trackShare from '../../assets/media/product/scenarios/track-share.svg';

const OutageTemplate = ({ item }) => {
  const [isFlipped, setFlipped] = useState(false);

  const getIcon = () => {
    // eslint-disable-next-line default-case
    switch (item.type) {
      case 'Blackhole':
        return blackhole;
      case 'CPU':
        return cpu;
      case 'DNS':
        return dns;
      case 'Latency':
        return latency;
      case 'Packet Loss':
        return packetLoss;
      case 'Shutdown':
        return shutdown;
    }
  };

  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <div
      role="button"
      onClick={() => setFlipped(!isFlipped)}
      tabIndex="-1"
      className={cx('product__outageTemplate', { 'is-flipped': isFlipped })}
    >
      <div className="product__outageTemplate-flipper">
        <div className="product__outageTemplate-front">
          <h4 className="u-textMedium">{item.title}</h4>
          <div className="product__outageTemplate-description">
            <Data markdown>{item.blurb}</Data>
          </div>
          <div className="product__outageTemplate-type">
            <div
              className="product__outageTemplate-icon"
              style={{ backgroundImage: `url(${getIcon()})` }}
            />
            <div className="product__outageTemplate-title">{item.type}</div>
          </div>
        </div>
        <div className="product__outageTemplate-back u-contrast">
          <h4 className="u-textMedium">{item.outageTitle}</h4>
          <a
            href={item.outageLink}
            rel="noopener noreferrer"
            target="_blank"
            className="u-textUnderline u-textSmall u-link"
            onClick={e => e.stopPropagation()}
          >
            Read the report
          </a>
        </div>
      </div>
    </div>
  );
  /* eslint-enable jsx-a11y/click-events-have-key-events */
};

OutageTemplate.propTypes = {
  item: PropTypes.object.isRequired,
};

const ProductScenarios = () => {
  const {
    file: {
      childDataYaml: { scenarios: pageData },
    },
  } = useStaticQuery(graphql`
    query ProductScenariosPageData {
      file(name: { eq: "product" }) {
        childDataYaml {
          scenarios {
            overview {
              heading
              blurb
              cta {
                label
              }
            }
            outageTemplates {
              heading
              blurb
              items {
                title
                type
                blurb
                outageTitle
                outageLink
              }
            }
            withstand {
              heading
              blurb
              items {
                blurb
              }
            }
            buildShare {
              heading
              blurb
              items {
                blurb
              }
            }
            scale {
              heading
              blurb
              dialHeading
              magnitudeHeading
            }
            hypothesizeObserve {
              heading
              blurb
            }
            trackShare {
              heading
              blurb
            }
          }
        }
      }
    }
  `);

  const [scenariosModalOpen, setScenariosModalOpen] = useState(false);
  const pageController = useRef();
  const dialUpRef = useRef();
  const magnitudeRef = useRef();
  const [dialUpPlaying, setDialUpPlaying] = useState(false);
  const [magnitudePlaying, setMagnitudePlaying] = useState(false);

  useEffect(() => {
    pageController.current = new ScrollMagic.Controller();

    const dialUpScene = new ScrollMagic.Scene({
      triggerElement: dialUpRef.current,
      triggerHook: 0.75,
      reverse: false,
    });

    dialUpScene.on('enter', () => {
      setDialUpPlaying(true);
    });

    dialUpScene.addTo(pageController.current);

    const magnitudeScene = new ScrollMagic.Scene({
      triggerElement: magnitudeRef.current,
      triggerHook: 0.75,
      reverse: false,
    });

    magnitudeScene.on('enter', () => {
      setMagnitudePlaying(true);
    });

    magnitudeScene.addTo(pageController.current);

    return () => {
      if (pageController.current) {
        pageController.current.destroy();
        pageController.current = null;
      }
    };
  }, []);

  return (
    <Fragment>
      <section className="section section--large">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.overview.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.overview.blurb}</Data>
          </div>
        </div>
        <div className="u-textCenter">
          <Button
            type="primary"
            onClick={() => {
              if (window.gtag) {
                window.gtag('event', 'Click', {
                  event_category: 'Scenarios Demo Video',
                  event_label: 'Product Scenarios tab',
                });
              }

              setScenariosModalOpen(true);
            }}
          >
            {pageData.overview.cta.label}
          </Button>
          <Modal
            open={scenariosModalOpen}
            onOutsideModalClick={() => setScenariosModalOpen(false)}
            className="shared__videoModal"
            size="large"
          >
            <ModalBody>
              <Close
                className="u-textContrast"
                onClick={() => setScenariosModalOpen(false)}
              />
              <div className="shared__responsiveVideo">
                <iframe
                  title="Gremlin Scenarios Demo"
                  width="640"
                  height="360"
                  src={config.scenariosVideoEmbed}
                  frameBorder="0"
                  allow="autoplay"
                />
              </div>
            </ModalBody>
          </Modal>
        </div>
        <div className="product__scenariosExpand">
          <img src={overview} alt="" />
        </div>
      </section>
      <section className="section section--large product__section">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.outageTemplates.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.outageTemplates.blurb}</Data>
          </div>
        </div>
        <div className="product__outageTemplates-wrapper">
          <div className="shared__overflowScroll u-pv--small">
            {pageData.outageTemplates.items.map(item => (
              <OutageTemplate key={item.title} item={item} />
            ))}
          </div>
        </div>
      </section>
      <section className="section section--large product__section">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.withstand.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.withstand.blurb}</Data>
          </div>
        </div>
        <div className="container container--small">
          <List className="product__plusList has-columns">
            {pageData.withstand.items.map(item => (
              <ListItem key={item.blurb}>
                <Data markdown>{item.blurb}</Data>
              </ListItem>
            ))}
          </List>
        </div>
      </section>
      <section className="section section--large product__section">
        <div className="product__gridBlock product__gridBlock--large">
          <img
            src={buildShare}
            className="product__gridBlock--buildShare"
            alt=""
          />
          <div className="product__gridBlock-content">
            <section className="section-titleWrapper section-titleWrapper--forceLeft@medium u-mb--large">
              <h3 className="section-title u-textFluid--h2-h3">
                <Data>{pageData.buildShare.heading}</Data>
              </h3>
              <div className="section-titleSub">
                <Data markdown>{pageData.buildShare.blurb}</Data>
              </div>
            </section>
            <hr className="product__hr" />
            <List className="product__plusList">
              {pageData.buildShare.items.map(item => (
                <ListItem key={item.blurb}>
                  <Data markdown>{item.blurb}</Data>
                </ListItem>
              ))}
            </List>
          </div>
        </div>
      </section>
      <section className="section section--large product__section">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.scale.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.scale.blurb}</Data>
          </div>
        </div>
        <div className="product__scaleGrid">
          <div>
            <h4 className="product__scaleGrid-header">
              {pageData.scale.dialHeading}
            </h4>
            <div
              className="product__scaleGrid-image--first"
              style={{ backgroundImage: `url(${dialUpBlob})` }}
            >
              <div ref={dialUpRef}>
                <ChooseHosts play={dialUpPlaying} />
              </div>
            </div>
          </div>
          <div>
            <h4 className="product__scaleGrid-header">
              {pageData.scale.magnitudeHeading}
            </h4>
            <div
              className="product__scaleGrid-image"
              style={{ backgroundImage: `url(${magnitudeBlob})` }}
            >
              <div ref={magnitudeRef}>
                <AttackConfig play={magnitudePlaying} />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section section--large product__section">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.hypothesizeObserve.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.hypothesizeObserve.blurb}</Data>
          </div>
        </div>
        <img src={hypothesizeObserve} alt="" style={{ width: '100%' }} />
      </section>
      <section className="section section--large product__section">
        <div className="section-titleWrapper">
          <h3 className="section-title u-textFluid--h2-h3">
            <Data>{pageData.trackShare.heading}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{pageData.trackShare.blurb}</Data>
          </div>
        </div>
        <img src={trackShare} alt="" style={{ width: '100%' }} />
      </section>
    </Fragment>
  );
};

export default ProductScenarios;
